import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // In production this would be sent to Sentry or similar
    console.error('[ErrorBoundary] Uncaught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const errorMessage = this.state.error?.message;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 bg-background">
        <div className="flex flex-col items-center text-center max-w-sm w-full">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mb-6">
            <AlertTriangle className="h-10 w-10 text-amber-500" />
          </div>

          {/* Title */}
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Algo deu errado
          </h1>

          {/* Subtitle */}
          <p className="text-sm text-gray-500 mb-8 leading-relaxed">
            Ocorreu um erro inesperado. Tente novamente.
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3 w-full">
            <button
              onClick={this.handleRetry}
              className="w-full py-3 px-4 bg-primary text-white font-semibold rounded-xl active:opacity-90 transition-opacity"
              style={{ backgroundColor: 'hsl(221, 83%, 53%)' }}
            >
              Tentar novamente
            </button>

            <button
              onClick={this.handleGoHome}
              className="w-full py-3 px-4 text-sm font-medium text-gray-500 active:text-gray-700 transition-colors"
            >
              Voltar ao inicio
            </button>
          </div>

          {/* Debug details */}
          {errorMessage && (
            <details className="mt-8 w-full text-left">
              <summary className="text-xs text-gray-400 cursor-pointer select-none">
                Detalhes do erro
              </summary>
              <pre className="mt-2 text-xs text-gray-400 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                {errorMessage}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
